/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import Chip from '@ion/dls/src/components/chip/Chip';
import {SystemInRangeIcon} from '@ion/dls/src/icons/current/SystemInRangeIcon';
import {SystemOutOfRangeIcon} from '@ion/dls/src/icons/current/SystemOutOfRangeIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {IconSize} from '@ion/dls/src/types/types';

export type RangeStatus = 'in' | 'out';

export interface RangeStatusBadgeProps {
  range: RangeStatus;
  // Tag Title rows use the 16px badge; Tool Card headers use the 24px one -
  // per ION-7463's icon-size facts (16px range badge, 24px asset type/header) ...
  size?: 16 | 24;
  // overrides the default 'In Range'/'Out of Range' label text while the icon
  // still tracks `range` - the Nearby tab's own Tag Title rows substitute an
  // assignment-status label ('Unassigned', or another asset's name) in this
  // slot while still showing the (always green, since the Nearby tab only
  // lists in-range tags) range icon; see TagTitleRow.tsx ...
  label?: string;
}

interface StyleProps {
  size: 16 | 24;
}

// wraps Chip.tsx with fixed props/overrides rather than copying its
// internals - MUI's Chip always renders its `icon` slot before `label`,
// while Figma's "Range Status" frame orders label-then-icon, so `root` is
// flipped with `flexDirection: row-reverse` (keeping the same `icon`/`label`
// slots Chip already renders) instead of reaching into Chip's own markup.
// Chip.tsx's own `ChipWrapper` only forwards its `root`/`label`/`deleteIcon`
// class overrides to the underlying MUI `<Chip>` (see its render) - not
// `icon` - so the icon's own 16/24px sizing is driven directly by the
// `size` passed to `RangeIcon` below instead of a `classes.icon` override
// here, which `ChipWrapper` would silently drop ...
const useStyles = makeStyles(() => ({
  root : {
    display         : 'inline-flex',
    flexDirection   : 'row-reverse',
    alignItems      : 'center',
    gap             : '8px',
    height          : 'auto',
    padding         : '0 !important',
    margin          : '0 !important',
    border          : 'none !important',
    borderRadius    : '0 !important',
    backgroundColor : 'transparent !important',
    // a status, not a tag - none of the chip's tag shadow ...
    boxShadow       : 'none !important'
  },
  label : {
    padding       : '0 !important',
    fontFamily    : 'Inter',
    fontWeight    : 600,
    letterSpacing : '0.5px',
    color         : dlsColor('grey2'),
    fontSize      : ({size}: StyleProps) => size === 24 ? '13px' : '10px',
    lineHeight    : ({size}: StyleProps) => size === 24 ? '16px' : '14px'
  }
}));

/**
 * RangeStatusBadge renders the shared green "In Range" / red "Out of Range"
 * icon+label used by both `TagTitleRow` (16px) and `ToolCard` (24px) in the
 * Tags accordion (ION-7463). The icon color always tracks `range`; `label`
 * may be overridden to show assignment-status text instead (see the
 * `label` prop doc) while keeping the range-driven icon.
 *
 * ```typescript
 *
 *   import {RangeStatusBadge} from
 *     '@ion/dls/src/components/assetdetails/current/RangeStatusBadge';
 *   ...
 *
 *   <RangeStatusBadge range='in' />
 *   <RangeStatusBadge range='out' size={24} />
 *   <RangeStatusBadge range='in' label='Unassigned' />
 *
 * ```
 *
 */
export const RangeStatusBadge = ({
  range,
  size = 16,
  label
}: RangeStatusBadgeProps) => {
  const classes    = useStyles({size});
  const isInRange  = range === 'in';
  const RangeIcon  = isInRange ? SystemInRangeIcon : SystemOutOfRangeIcon;
  const iconColor  = isInRange ? dlsColor('caGreen') : dlsColor('red');
  const iconSize   = size === 24 ? IconSize.medium : IconSize.small;
  const labelText  = label || (isInRange ? 'In Range' : 'Out of Range');

  return (
    <Chip disabled={false}
      isClickable={false}
      showCheckmark={false}
      label={labelText}
      icon={<RangeIcon size={iconSize} color={iconColor} />}
      classes={{root: classes.root, label: classes.label}} />
  );
};

export default memo(RangeStatusBadge);
